import { GAME_2048_DISPLAY_KEY, parse2048DisplayMode, type Game2048DisplayMode } from './game2048Appearance';
import {
  canRestorePuzzleState,
  parseDailyPuzzleIdentity,
  puzzleIdentitiesMatch,
  type DailyPuzzleIdentity
} from './dailyPuzzle';
import {
  create2048State,
  GAME_2048_TARGETS,
  get2048LargestTile,
  get2048Outcome,
  get2048Progress,
  get2048Target,
  parse2048State,
  validate2048State,
  type Game2048Difficulty,
  type Game2048EngineState,
  type Game2048MoveResult,
  type Game2048Outcome
} from './game2048';

export const GAME_2048_STORAGE = {
  session: 'game-2048-session',
  archive: 'game-2048-pending-runs',
  seed: 'game-2048-seed',
  identity: 'game-2048-identity',
  state: 'game-2048-state',
  previousState: 'game-2048-previous-state',
  undosUsed: 'game-2048-undos-used',
  elapsedTime: 'game-2048-elapsed-time',
  completed: 'game-2048-completed',
  bestScore: 'game-2048-best-score'
} as const;

export interface Game2048Snapshot {
  score: number;
  moves: number;
  undosUsed: number;
  elapsedTime: number;
}

export interface Game2048Milestone {
  difficulty: Game2048Difficulty;
  status: 'pending' | 'failed' | 'saved' | 'legacy';
  snapshot: Game2048Snapshot | null;
}

export interface Game2048Session extends Game2048Snapshot {
  version: 1;
  displayMode: Game2048DisplayMode;
  runId: string;
  principal: string | null;
  identity: DailyPuzzleIdentity;
  engine: Game2048EngineState;
  previousEngine: Game2048EngineState | null;
  highestTile: number;
  outcome: Game2048Outcome;
  milestones: Game2048Milestone[];
  recordStatus: 'none' | 'pending' | 'failed' | 'saved';
}

export const parse2048Integer = (value: string | null, name: string): number => {
  if (value === null) {
    return 0;
  }
  if (!/^\d+$/.test(value) || !Number.isSafeInteger(Number(value))) {
    throw new Error(`Invalid saved 2048 ${name}`);
  }
  return Number(value);
};

export const create2048Session = (identity: DailyPuzzleIdentity, principal: string | null = null): Game2048Session => {
  const engine = create2048State(identity.seed);
  return {
    version: 1,
    displayMode: parse2048DisplayMode(localStorage.getItem(GAME_2048_DISPLAY_KEY)),
    runId: crypto.randomUUID(),
    principal,
    identity,
    engine,
    previousEngine: null,
    score: 0,
    moves: 0,
    undosUsed: 0,
    elapsedTime: 0,
    highestTile: get2048LargestTile(engine.board),
    outcome: 'active',
    milestones: [],
    recordStatus: 'none'
  };
};

const snapshot2048 = (session: Game2048Session): Game2048Snapshot => ({
  score: session.engine.score,
  moves: session.engine.moves,
  undosUsed: session.undosUsed,
  elapsedTime: session.elapsedTime
});

export const advance2048Session = (session: Game2048Session, result: Game2048MoveResult): Game2048Session => {
  if (session.outcome !== 'active') {
    throw new Error('Cannot advance a finished 2048 run');
  }
  if (!result.moved) {
    return session;
  }
  const highestTile = Math.max(session.highestTile, get2048LargestTile(result.state.board));
  const outcome = get2048Outcome(result.state.board);
  const next: Game2048Session = {
    ...session,
    engine: result.state,
    previousEngine: outcome === 'active' ? session.engine : null,
    score: result.state.score,
    moves: result.state.moves,
    highestTile,
    outcome,
    recordStatus: outcome !== 'active' && highestTile > 4096 ? 'pending' : 'none'
  };
  const crossed = GAME_2048_TARGETS.slice(0, 4).flatMap((target, index): Game2048Milestone[] => (
    target > session.highestTile && target <= highestTile
      ? [{ difficulty: (index + 1) as Game2048Difficulty, status: 'pending', snapshot: snapshot2048(next) }]
      : []
  ));
  return { ...next, milestones: [...session.milestones, ...crossed] };
};

export const undo2048Session = (session: Game2048Session): Game2048Session => {
  if (session.outcome !== 'active' || session.previousEngine === null) {
    return session;
  }
  const undosUsed = session.undosUsed + 1;
  if (!Number.isSafeInteger(undosUsed)) {
    throw new Error('Unsafe 2048 undo count');
  }
  return {
    ...session,
    engine: session.previousEngine,
    score: session.previousEngine.score,
    moves: session.previousEngine.moves,
    previousEngine: null,
    undosUsed
  };
};

const requireRecord = (value: unknown): Record<string, unknown> => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error('Invalid saved 2048 session');
  }
  return value as Record<string, unknown>;
};

const validateSnapshot = (value: unknown): Game2048Snapshot => {
  const snapshot = requireRecord(value);
  if (['score', 'moves', 'undosUsed', 'elapsedTime'].some(key => (
    typeof snapshot[key] !== 'number' || !Number.isSafeInteger(snapshot[key]) || Number(snapshot[key]) < 0
  ))) {
    throw new Error('Invalid saved 2048 snapshot');
  }
  return { score: Number(snapshot.score), moves: Number(snapshot.moves), undosUsed: Number(snapshot.undosUsed), elapsedTime: Number(snapshot.elapsedTime) };
};

const restoreDisplayMode = (value: unknown): Game2048DisplayMode => {
  if (value === undefined) {
    // Sessions saved before per-run display modes inherit the existing preference.
    return parse2048DisplayMode(localStorage.getItem(GAME_2048_DISPLAY_KEY));
  }
  if (typeof value !== 'string') {
    throw new Error('Invalid saved 2048 display mode');
  }
  return parse2048DisplayMode(value);
};

export const parse2048Session = (serialized: string): Game2048Session => {
  const value = requireRecord(JSON.parse(serialized));
  const snapshot = validateSnapshot(value);
  const engine = validate2048State(value.engine);
  const previousEngine = value.previousEngine === null ? null : validate2048State(value.previousEngine);
  const identity = parseDailyPuzzleIdentity(JSON.stringify(value.identity));
  if (
    value.version !== 1 || identity.gameType !== '2048' ||
    typeof value.runId !== 'string' || !/^[\da-f]{8}-[\da-f]{4}-4[\da-f]{3}-[89ab][\da-f]{3}-[\da-f]{12}$/i.test(value.runId) ||
    (value.principal !== null && (typeof value.principal !== 'string' || value.principal.length === 0)) ||
    typeof value.highestTile !== 'number' || value.highestTile < get2048LargestTile(engine.board) ||
    value.highestTile < get2048LargestTile(previousEngine?.board ?? [0]) ||
    value.outcome !== get2048Outcome(engine.board) ||
    (value.outcome !== 'active' && previousEngine !== null) ||
    snapshot.score !== engine.score || snapshot.moves !== engine.moves ||
    !Array.isArray(value.milestones) || !['none', 'pending', 'failed', 'saved'].includes(String(value.recordStatus))
  ) {
    throw new Error('Invalid saved 2048 session');
  }
  get2048Progress(value.highestTile);
  const qualifies = value.outcome !== 'active' && value.highestTile > 4096;
  if ((value.recordStatus === 'none') === qualifies) {
    throw new Error('Invalid saved 2048 record status');
  }
  const milestones = value.milestones.map((item, index): Game2048Milestone => {
    const milestone = requireRecord(item);
    if (
      milestone.difficulty !== index + 1 || index >= 4 ||
      get2048Target(index + 1) > Number(value.highestTile) ||
      !['legacy', 'pending', 'failed', 'saved'].includes(String(milestone.status)) ||
      (milestone.snapshot === null && milestone.status !== 'legacy')
    ) {
      throw new Error('Invalid saved 2048 milestone');
    }
    return {
      difficulty: (index + 1) as Game2048Difficulty,
      status: milestone.status as Game2048Milestone['status'],
      snapshot: milestone.snapshot === null ? null : validateSnapshot(milestone.snapshot)
    };
  });
  if (milestones.length !== GAME_2048_TARGETS.slice(0, 4).filter(target => target <= Number(value.highestTile)).length) {
    throw new Error('Missing saved 2048 milestones');
  }
  return {
    ...snapshot,
    version: 1,
    displayMode: restoreDisplayMode(value.displayMode),
    runId: value.runId,
    principal: value.principal as string | null,
    identity,
    engine,
    previousEngine,
    highestTile: value.highestTile,
    outcome: value.outcome as Game2048Outcome,
    milestones,
    recordStatus: value.recordStatus as Game2048Session['recordStatus']
  };
};

const migrate2048Session = (identity: DailyPuzzleIdentity, engine: Game2048EngineState): Game2048Session => {
  const completionFlag = localStorage.getItem(GAME_2048_STORAGE.completed);
  if (completionFlag !== null && !['true', 'false'].includes(completionFlag)) {
    throw new Error('Invalid saved 2048 completion state');
  }
  const largestTile = get2048LargestTile(engine.board);
  if (completionFlag === 'true' && largestTile < get2048Target(identity.difficulty)) {
    throw new Error('Invalid saved 2048 completion state');
  }
  const previous = localStorage.getItem(GAME_2048_STORAGE.previousState);
  const previousEngine = previous === null ? null : parse2048State(previous);
  const highestTile = Math.max(largestTile, get2048LargestTile(previousEngine?.board ?? [0]));
  const outcome = get2048Outcome(engine.board);
  const session: Game2048Session = {
    ...create2048Session(identity),
    engine,
    previousEngine: outcome === 'active' ? previousEngine : null,
    score: engine.score,
    moves: engine.moves,
    undosUsed: parse2048Integer(localStorage.getItem(GAME_2048_STORAGE.undosUsed), 'undo count'),
    elapsedTime: parse2048Integer(localStorage.getItem(GAME_2048_STORAGE.elapsedTime), 'elapsed time'),
    highestTile,
    outcome,
    recordStatus: outcome !== 'active' && highestTile > 4096 ? 'pending' : 'none'
  };
  session.milestones = GAME_2048_TARGETS.slice(0, 4).flatMap((target, index): Game2048Milestone[] => {
    if (target > highestTile) {
      return [];
    }
    const isKnownCompletion = index + 1 === identity.difficulty && target <= largestTile;
    return [{
      difficulty: (index + 1) as Game2048Difficulty,
      status: isKnownCompletion ? (completionFlag === 'true' ? 'saved' : 'pending') : 'legacy',
      snapshot: isKnownCompletion ? snapshot2048(session) : null
    }];
  });
  return session;
};

export const has2048PendingSaves = (session: Game2048Session): boolean => (
  ['pending', 'failed'].includes(session.recordStatus) || session.milestones.some(milestone => ['pending', 'failed'].includes(milestone.status))
);

export const read2048PendingRuns = (): Game2048Session[] => {
  const value: unknown = JSON.parse(localStorage.getItem(GAME_2048_STORAGE.archive) ?? '[]');
  if (!Array.isArray(value)) {
    throw new Error('Invalid pending 2048 runs');
  }
  return value.map(item => parse2048Session(JSON.stringify(item)));
};

export const archive2048Session = (session: Game2048Session): void => {
  const runs = read2048PendingRuns().filter(run => run.runId !== session.runId);
  if (has2048PendingSaves(session)) {
    runs.push(session);
  }
  localStorage.setItem(GAME_2048_STORAGE.archive, JSON.stringify(runs));
};

export const load2048Session = (identity: DailyPuzzleIdentity): Game2048Session => {
  const serialized = localStorage.getItem(GAME_2048_STORAGE.session);
  if (serialized !== null) {
    const session = parse2048Session(serialized);
    if (puzzleIdentitiesMatch(session.identity, identity)) {
      return session;
    }
    archive2048Session(session);
  }
  const savedSeed = localStorage.getItem(GAME_2048_STORAGE.seed);
  const engine = localStorage.getItem(GAME_2048_STORAGE.state);
  if (serialized === null && engine !== null && canRestorePuzzleState(
    localStorage.getItem(GAME_2048_STORAGE.identity),
    savedSeed === null ? undefined : parse2048Integer(savedSeed, 'seed'),
    identity
  )) {
    return migrate2048Session(identity, parse2048State(engine));
  }
  return create2048Session(identity);
};

export const persist2048Session = (session: Game2048Session): void => {
  localStorage.setItem(GAME_2048_STORAGE.session, JSON.stringify(session));
  localStorage.setItem(GAME_2048_STORAGE.seed, String(session.identity.seed));
  localStorage.setItem(GAME_2048_STORAGE.identity, JSON.stringify(session.identity));
  localStorage.setItem(GAME_2048_STORAGE.state, JSON.stringify(session.engine));
  localStorage.setItem(GAME_2048_STORAGE.undosUsed, String(session.undosUsed));
  localStorage.setItem(GAME_2048_STORAGE.elapsedTime, String(session.elapsedTime));
  localStorage.setItem(GAME_2048_STORAGE.completed, String(session.highestTile >= 512));
  if (session.previousEngine === null) {
    localStorage.removeItem(GAME_2048_STORAGE.previousState);
  } else {
    localStorage.setItem(GAME_2048_STORAGE.previousState, JSON.stringify(session.previousEngine));
  }
  if (session.highestTile >= 512 || session.outcome !== 'active') {
    const best = parse2048Integer(localStorage.getItem(GAME_2048_STORAGE.bestScore), 'best score');
    localStorage.setItem(GAME_2048_STORAGE.bestScore, String(Math.max(best, session.engine.score)));
  }
};
